import type { ComponentType, ReactElement, ReactNode } from 'react'
import type {
  SidebarTreeBranchProps,
  SidebarTreeLeafProps,
  SidebarTreeProps,
} from '../components/sidebar-tree'
import { uStorybookUI } from './storybook-ui-u'

type IC = ComponentType<{ className?: string }>

type ClassInput =
  | string
  | number
  | boolean
  | null
  | undefined
  | Record<string, boolean | null | undefined>
  | ClassInput[]

/**
 * Surface/border tokens for storybook chrome (panel bg, dividers). Text typography
 * is NOT here — it comes from the `Text` component below so the type scale has a
 * single source of truth. Defaults to the UI design system; override via
 * `setStorybookUI` when embedding storybook in another host.
 */
export interface StorybookTokens {
  color: {
    /** Border / divider color. */
    border: string
    /** Panel/popover background. */
    surface: string
  }
}

/** Semantic text roles shared with the UI lib's `Text` component. */
type TextSize = 'title' | 'body' | 'caption' | 'label' | 'hint'
type TextTone = 'default' | 'muted'

export interface StorybookUIConfig {
  cn: (...inputs: ClassInput[]) => string

  /** Overridable surface/border tokens; defaults to the UI lib's design system. */
  tokens: StorybookTokens

  /** Text primitive carrying the shared type scale (size) + tone. */
  Text: ComponentType<{
    size?: TextSize
    tone?: TextTone
    className?: string
    title?: string
    children: ReactNode
  }>

  icons: {
    X: IC
    Copy: IC
    Pencil: IC
    RotateCcw: IC
    RotateCw: IC
    ChevronDown: IC
    ChevronRight: IC
    FileText: IC
    Layers: IC
    Layout: IC
    Puzzle: IC
    BookMarked: IC
    ChevronsUpDown: IC
    ServerCog: IC
    Microscope: IC
    SearchCode: IC
    TreePalm: IC
    Route: IC
    Activity: IC
    Sun: IC
    Moon: IC
    WifiOff: IC
    Rabbit: IC
    Turtle: IC
    CircleX: IC
    Minus: IC
  }

  Sidebar: {
    Provider: ComponentType<{
      children: ReactNode
      open?: boolean
      onOpenChange?: (open: boolean) => void
      revealOnHover?: boolean
      className?: string
    }>
    Root: ComponentType<{
      side?: 'left' | 'right'
      variant?: 'sidebar' | 'floating' | 'inset'
      children: ReactNode
    }>
    Inset: ComponentType<{
      className?: string
      children: ReactNode
      ref?: React.Ref<HTMLElement>
      tabIndex?: number
    }>
    Trigger: ComponentType<{ className?: string }>
    Content: ComponentType<{ className?: string; children: ReactNode }>
    Header: ComponentType<{ children: ReactNode }>
    Group: ComponentType<{ className?: string; children: ReactNode }>
    GroupLabel: ComponentType<{ className?: string; children: ReactNode }>
    GroupContent: ComponentType<{ children: ReactNode }>
    Menu: ComponentType<{ className?: string; children: ReactNode }>
    // oxlint-disable-next-line typescript/no-explicit-any -- abstraction layer — extra props (e.g. render) forwarded via spread
    MenuButton: ComponentType<any>
    MenuItem: ComponentType<{ children: ReactNode }>
    MenuSub: ComponentType<{ className?: string; children: ReactNode }>
    MenuSubItem: ComponentType<{ children: ReactNode }>
    Rail: ComponentType<Record<string, never>>
    Tree: ComponentType<SidebarTreeProps>
    TreeBranch: ComponentType<SidebarTreeBranchProps>
    TreeLeaf: ComponentType<SidebarTreeLeafProps>
  }

  Collapsible: {
    Root: ComponentType<{
      className?: string
      open?: boolean
      onOpenChange?: (open: boolean) => void
      children: ReactNode
    }>
    Trigger: ComponentType<{
      // oxlint-disable-next-line typescript/no-explicit-any -- abstraction layer accepts any render fn
      render?: ((props: any) => ReactElement) | ReactElement
      children?: ReactNode
    }>
    Content: ComponentType<{ children: ReactNode }>
  }

  DropdownMenu: {
    Root: ComponentType<{ children: ReactNode }>
    Trigger: ComponentType<{
      // oxlint-disable-next-line typescript/no-explicit-any -- abstraction layer accepts any render fn
      render?: ((props: any) => ReactElement) | ReactElement
      children?: ReactNode
    }>
    Content: ComponentType<{
      className?: string
      align?: 'start' | 'center' | 'end'
      side?: 'top' | 'right' | 'bottom' | 'left'
      sideOffset?: number
      children: ReactNode
    }>
    Item: ComponentType<{
      className?: string
      onClick?: () => void
      children: ReactNode
    }>
    Sub: ComponentType<{ children: ReactNode }>
    SubTrigger: ComponentType<{
      className?: string
      children: ReactNode
    }>
    SubContent: ComponentType<{
      className?: string
      children: ReactNode
    }>
    Group: ComponentType<{ children: ReactNode }>
    Label: ComponentType<{ className?: string; children: ReactNode }>
    RadioGroup: ComponentType<{
      value?: string
      onValueChange?: (v: string) => void
      children: ReactNode
    }>
    RadioItem: ComponentType<{
      value: string
      closeOnClick?: boolean
      children: ReactNode
    }>
    Separator: ComponentType<Record<string, never>>
    Shortcut: ComponentType<{ children: ReactNode }>
  }

  Breadcrumb: {
    Root: ComponentType<{ className?: string; children: ReactNode }>
    List: ComponentType<{ className?: string; children: ReactNode }>
    Item: ComponentType<{ className?: string; children: ReactNode }>
    Page: ComponentType<{ className?: string; children: ReactNode }>
    Separator: ComponentType<Record<string, never>>
  }

  Button: ComponentType<{
    variant?: 'text' | 'filled' | 'outlined'
    semanticRole?:
      | 'primary'
      | 'secondary'
      | 'success'
      | 'error'
      | 'warning'
      | 'info'
    onClick?: () => void
    className?: string
    children: ReactNode
  }>

  IconButton: ComponentType<{
    variant?: 'text' | 'filled' | 'outlined'
    semanticRole?:
      | 'primary'
      | 'secondary'
      | 'success'
      | 'error'
      | 'warning'
      | 'info'
    size?: 'sm' | 'default'
    onClick?: () => void
    onPointerDown?: (e: React.PointerEvent) => void
    className?: string
    type?: 'button' | 'reset' | 'submit'
    tooltipSide?: 'top' | 'bottom' | 'left' | 'right'
    tooltipSideOffset?: number
    children: ReactNode
  }>

  Separator: ComponentType<{
    orientation?: 'horizontal' | 'vertical'
    className?: string
  }>

  Input: ComponentType<
    React.ComponentProps<'input'> & {
      minimal?: boolean
      leading?: ReactNode
      inputRef?: React.Ref<HTMLInputElement>
    }
  >

  Checkbox: ComponentType<{
    checked?: boolean
    onCheckedChange?: (checked: boolean) => void
    className?: string
  }>

  Switch: ComponentType<{
    checked?: boolean
    onCheckedChange?: (checked: boolean) => void
    className?: string
    id?: string
    'data-testid'?: string
  }>

  Select: {
    Root: ComponentType<{
      value?: unknown
      onValueChange?: (value: unknown) => void
      multiple?: boolean
      minimal?: boolean
      children: ReactNode
    }>
    Trigger: ComponentType<{
      className?: string
      children: ReactNode
    }>
    Value: ComponentType<{
      placeholder?: string
      // oxlint-disable-next-line typescript/no-explicit-any -- abstraction layer; value shape is polymorphic per schema field
      children?: (value: any) => ReactNode
    }>
    Popup: ComponentType<{ className?: string; children: ReactNode }>
    Item: ComponentType<{
      value: unknown
      className?: string
      children: ReactNode
    }>
  }

  Emoji: ComponentType<{ children: string }>
}

export let SB: StorybookUIConfig = uStorybookUI

export function setStorybookUI(config: StorybookUIConfig) {
  SB = config
}
